import React from "react";
import { useLang } from "@/i18n";

/**
 * Phase A17-G — 5-state segmented control for view scopes on admin pages.
 * Maps to backend `view_scope` query param:
 *   "production" | "test_qa" | "spam" | "inactive" | "all"
 *
 * Backwards compatible: passing legacy values "test" / "all" still works
 * via `viewToParams` mapping below — older callers that haven't migrated
 * to the unified `view_scope` param continue to receive the same data.
 *
 * Pure presentation: the parent owns `value` and `onChange`. No data
 * mutation, no API call here.
 */
const SCOPE_THEME = {
  production: "bg-white text-slate-900 shadow-sm",
  test_qa: "bg-amber-100 text-amber-800",
  spam: "bg-rose-100 text-rose-800",
  inactive: "bg-slate-200 text-slate-700",
  all: "bg-slate-700 text-white",
};

export const ViewScopeTabs = ({ value = "production", onChange, testIdPrefix = "view-scope", compact = false }) => {
  const { t } = useLang();
  const options = [
    { key: "production", label: t("phase2.viewProduction") },
    { key: "test_qa", label: t("phase2.viewTestQa") },
    { key: "spam", label: t("phase2.viewSpam") },
    { key: "inactive", label: t("phase2.viewInactive") },
    { key: "all", label: t("phase2.viewAll") },
  ];

  return (
    <div
      role="tablist"
      aria-label={t("phase2.viewLabel")}
      className="inline-flex items-center gap-1 rounded-full bg-slate-100 p-1"
      data-testid={`${testIdPrefix}-tabs`}
    >
      {options.map((opt) => {
        const active = value === opt.key;
        return (
          <button
            key={opt.key}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange?.(opt.key)}
            data-testid={`${testIdPrefix}-${opt.key}`}
            className={`${compact ? "px-2" : "px-3"} py-1 text-xs font-medium rounded-full transition ${
              active ? SCOPE_THEME[opt.key] : "text-slate-600 hover:text-slate-900"
            }`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
};

/** Translate a `view` value into the query params expected by the backend.
 *  Returns the unified `view_scope` parameter the backend now accepts. */
export const viewToParams = (view) => {
  // Map legacy "test" → "test_qa" so older callers keep working.
  const scope = view === "test" ? "test_qa" : view;
  if (!scope || scope === "production") return { view_scope: "production" };
  return { view_scope: scope };
};

export default ViewScopeTabs;
